import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buyerSmartImportApi,
  type BuyerSmartImportBatchDto,
  type BuyerSmartImportReviewStateDto,
  type BuyerSmartImportSource,
} from "@/api/buyerSmartImport";
import { ApiError, extractErrorMessage } from "@/api/client";
import { isSessionExpired, retryUnlessSessionExpired } from "@/api/polling";
import { buyerKeys } from "./buyerKeys";
import { smartImportKeys } from "./smartImportKeys";

/**
 * Buyer Smart Import — the reads and the two writes.
 *
 * <p>🔴 <b>The cache rule, and it is the whole of D20.</b> Committing does NOT
 * invalidate the buyer caches: the commit returns a QUEUED batch, and at that
 * moment not one buyer exists. The rows appear when the background job
 * finishes, which no client write can observe — so the POLL is what invalidates
 * `buyerKeys.all`, at the transition to Completed, and an undo invalidates it
 * again because archiving is equally a change to the book. Invalidating on the
 * queued response instead would refetch every buyer list a second before the
 * import wrote anything, and then never again.</p>
 */

/** How often an in-flight batch is re-read. */
export const SMART_IMPORT_POLL_MS = 2000;

/** The 10 MB file cap (D1), checked client-side so the refusal is a sentence. */
export const SMART_IMPORT_MAX_FILE_BYTES = 10 * 1024 * 1024;

/**
 * A friendly sentence for anything this feature can fail with.
 *
 * <p>A 413 is the one status whose body is written by the web server rather
 * than by the API, so it arrives with no envelope and no message — and "Request
 * failed (HTTP 413)" is exactly the raw refusal the contract says an over-size
 * upload must not produce.</p>
 */
export function smartImportErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 413) {
    return "That file is too large to read. The limit is 10 MB — split it and import each part.";
  }
  return extractErrorMessage(error);
}

/** True once a batch has stopped moving on its own. */
export function isTerminalBatchStatus(status: BuyerSmartImportBatchDto["status"]): boolean {
  return status === "Completed" || status === "Failed" || status === "Undone";
}

/**
 * Hold a value still for `ms` after it stops changing.
 *
 * <p>The review state is the dry-run's cache key, and typing a sentinel list
 * changes it on every keystroke. Without this each character is its own
 * round-trip against a 10,000-row file.</p>
 */
export function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(handle);
  }, [value, ms]);
  return settled;
}

/**
 * Read the file and propose a mapping.
 *
 * <p>A mutation rather than a query because it is an ACTION the operator takes
 * with a file in hand: it has no key anything could refetch it under, and
 * re-running it must re-ask the model rather than re-serve an answer.</p>
 */
export interface AnalyzeSmartImportVariables {
  source: BuyerSmartImportSource;
  /**
   * Skip the exact-match and plan-reuse paths and ask the model — what "Map
   * with AI instead" sends (D19). Default false, which is every ordinary
   * analyze.
   */
  forceAi?: boolean;
}

export function useAnalyzeSmartImport() {
  return useMutation({
    mutationFn: (vars: AnalyzeSmartImportVariables) =>
      buyerSmartImportApi.analyze(vars.source, vars.forceAi ?? false),
    // no-invalidation: analyze reads the uploaded file and creates nothing —
    // no batch, no buyer, no stored plan. There is no server state for it to
    // have made stale.
  });
}

/**
 * The server dry-run behind the review screen (D4).
 *
 * <p>Re-run whenever the debounced review signature moves, and cached under it
 * — so undoing a change re-serves the dry-run that change replaced rather than
 * paying for it twice.</p>
 */
export function useSmartImportPreview(
  source: BuyerSmartImportSource,
  contentHash: string | null,
  review: BuyerSmartImportReviewStateDto | null,
  reviewSignature: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: smartImportKeys.preview(contentHash ?? "", reviewSignature),
    queryFn: () => buyerSmartImportApi.preview(source, review!),
    enabled: enabled && Boolean(contentHash) && review != null,
    // The answer is a pure function of the file and the review state, both of
    // which are in the key, so there is nothing for a refetch to discover.
    staleTime: Infinity,
    retry: retryUnlessSessionExpired,
  });
}

/**
 * What each listing in the file implies about a buyer who enquired (D8).
 *
 * <p>Its own read, on its own narrower key, because it costs a Claude call:
 * see `smartImportKeys.interests`.</p>
 */
export function useSmartImportInterests(
  source: BuyerSmartImportSource,
  contentHash: string | null,
  review: BuyerSmartImportReviewStateDto | null,
  listingSignature: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: smartImportKeys.interests(contentHash ?? "", listingSignature),
    queryFn: () => buyerSmartImportApi.interests(source, review!),
    enabled: enabled && Boolean(contentHash) && review != null,
    staleTime: Infinity,
    retry: retryUnlessSessionExpired,
  });
}

/** The history list (D13). */
export function useSmartImportBatches() {
  return useQuery({
    queryKey: smartImportKeys.batches(),
    queryFn: () => buyerSmartImportApi.batches(),
  });
}

/**
 * One batch, polled while it is Queued or Running.
 *
 * <p>🔴 <b>This poll is the only thing that learns the import finished.</b> The
 * rows are created on the server by a background job, so there is no client
 * write to hang an invalidation off — the QueryClient runs at `staleTime: 5min`
 * with `refetchOnWindowFocus: false`, so without the effect below every buyer
 * list would keep serving its pre-import answer for minutes with no recovery.
 * It fires once per batch, tracked by id, so a later tick over the same
 * terminal status does nothing and StrictMode's double-invoked effects cannot
 * double it.</p>
 */
export function useSmartImportBatch(batchId: string | null) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: smartImportKeys.batch(batchId ?? ""),
    queryFn: () => buyerSmartImportApi.batch(batchId!),
    enabled: Boolean(batchId),
    refetchInterval: (q) => {
      if (isSessionExpired(q.state.error)) return false;
      const batch: BuyerSmartImportBatchDto | undefined = q.state.data;
      return batch && !isTerminalBatchStatus(batch.status) ? SMART_IMPORT_POLL_MS : false;
    },
    retry: retryUnlessSessionExpired,
  });

  const status = query.data?.status ?? null;
  const credited = useRef<string | null>(null);
  useEffect(() => {
    if (!batchId || status == null) return;
    if (credited.current === batchId) return;
    if (!isTerminalBatchStatus(status)) return;
    credited.current = batchId;
    // The history row carries this batch's status and counts, and it has no
    // interval of its own.
    void qc.invalidateQueries({ queryKey: smartImportKeys.batches() });
    // 🔴 Completed ONLY. A batch that failed created nothing, so refetching
    // every buyer list would be a round-trip that can only return what the
    // caller is already holding.
    if (status === "Completed") {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    }
  }, [batchId, status, qc]);

  return query;
}

/**
 * Queue the import.
 *
 * <p>Invalidates the history — the queued batch is a row that belongs in it
 * immediately — and deliberately NOT `buyerKeys.all`; see this file's header.</p>
 */
export function useCommitSmartImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      source: BuyerSmartImportSource;
      review: BuyerSmartImportReviewStateDto;
      contentHash: string;
      planHash: string;
    }) =>
      buyerSmartImportApi.commit(vars.source, vars.review, vars.contentHash, vars.planHash),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: smartImportKeys.batches() });
    },
  });
}

/**
 * Undo one batch (D13).
 *
 * <p>Invalidates the buyer caches as well as the history: an undo ARCHIVES
 * buyers, which moves every list, every stat tile and the assignment queue.</p>
 */
export function useUndoSmartImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) => buyerSmartImportApi.undo(batchId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: smartImportKeys.batches() });
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}
